import { useEffect, useCallback, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest } from "@/utils/api";
import { API_ENDPOINTS } from "@/config/api";
import { Loader } from "../ui/loader";
import { SubHeading } from "../ui/sub-heading";
import { Assessment, AssessmentQuestion } from "@/types";

interface AssessmentQuestionsStepProps {
  assessment: Assessment;
  onQuestionsGenerated: (questions: AssessmentQuestion[]) => void;
  questions: AssessmentQuestion[];
  onQuestionChange: (id: string, field: "text", value: string) => void;
  error: string;
  setIsGenerating: (isGenerating: boolean) => void;
  isGenerating: boolean;
  isLoading: boolean;
}

export function AssessmentQuestionsStep({
  assessment: jobSpec,
  onQuestionsGenerated,
  questions,
  onQuestionChange,
  error,
  setIsGenerating,
  isGenerating,
  isLoading,
}: AssessmentQuestionsStepProps) {
  const isInitialized = useRef(false);

  const handleGenerateQuestions = useCallback(async () => {
    setIsGenerating(true);
    try {
      const response = await apiRequest<
        Array<{
          cognitive_ability_questions: AssessmentQuestion[];
          personality_cultural_fit_questions: AssessmentQuestion[];
          technical_skill_questions: AssessmentQuestion[];
        }>
      >(API_ENDPOINTS.assessments.generate, {
        method: "POST",
        body: {
          title: jobSpec.title,
          skills: jobSpec.skills,
          ideal_candidate: jobSpec.ideal_candidate,
        },
      });

      // Flatten all questions into a single array and add IDs
      const allQuestions = [
        ...response[0].cognitive_ability_questions,
        ...response[1].personality_cultural_fit_questions,
        ...response[2].technical_skill_questions,
      ].map((q, index) => ({
        ...q,
        id: `question-${index}`,
      }));

      onQuestionsGenerated(allQuestions);
    } catch (err: any) {
      console.error("Failed to generate questions:", err);
    } finally {
      setIsGenerating(false);
    }
  }, [setIsGenerating, jobSpec, onQuestionsGenerated]);

  useEffect(() => {
    if (isInitialized.current) return;
    handleGenerateQuestions();
    isInitialized.current = true;
  }, [handleGenerateQuestions]);

  if (isGenerating) {
    return (
      <div className="flex justify-center py-8">
        <Loader title="Generating assessment questions" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SubHeading>Assessment questions</SubHeading>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-8">
        {questions.map((question, index) => (
          <div key={question.id} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Question {index + 1} ({question.type})
              </label>
              <Textarea
                value={question.text}
                onChange={(e) =>
                  onQuestionChange(question.id!, "text", e.target.value)
                }
                placeholder="Enter the question"
                disabled={isLoading}
              />
            </div>
            {question.type === "multiple-choice" && question.options && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Options</label>
                <div className="pl-4 space-y-2">
                  {question.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="text-sm text-gray-700">
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
